import { useMemo, useState } from 'react'
import { cn } from '@/lib/utils'
import type { CellView } from './model'

/**
 * The cell map — every cell that has a coordinate, placed.
 *
 * ## Why this is a projected scatter and not Mapbox
 *
 * The reading is *relative*: which side of the ward is behind, whether the slow cells are
 * next to each other or scattered. Basemap tiles answer none of that and cost a network
 * dependency, a token and a second of load on the one afternoon the page matters. What
 * this needs is position, and position is arithmetic.
 *
 * The projection is equirectangular with a cos(latitude) correction on x. Over a ward —
 * kilometres, not degrees — that is indistinguishable from anything more careful, and
 * without the correction the map is visibly stretched east-west near the equator.
 *
 * ## Colour here encodes state, and it is allowed to
 *
 * `shared/ui/charts.tsx` is right that identity should not be carried by the app's chart
 * hues. This is not identity — it is status (ahead / at pace / behind / silent), which is
 * the one job a reserved palette exists for, and every dot is also shaped: silence is a
 * hollow ring, because a cell nobody has heard from is an absence of data and must not
 * look like a measured zero. The legend names all four, so nothing is colour-alone.
 */

export type CellTone = 'ahead' | 'onpace' | 'behind' | 'silent'

/*
   Fills are held at ~35% because these marks overlap: cells in one catchment sit within a
   few hundred metres of each other, and at a heavier alpha a cluster reads as one solid
   blob rather than as six cells. The 2px stroke is what keeps each one individually
   countable through the pile.
*/
const TONE: Record<CellTone, { fill: string; stroke: string; open: string; closed: string }> = {
  ahead:  { fill: 'fill-emerald-500/35', stroke: 'stroke-emerald-600', open: 'Ahead of pace',      closed: 'Finished above 75%' },
  onpace: { fill: 'fill-sky-500/30',     stroke: 'stroke-sky-600',     open: 'At pace',            closed: 'Finished 50–75%' },
  behind: { fill: 'fill-rose-500/35',    stroke: 'stroke-rose-600',    open: 'Behind pace',        closed: 'Finished under 50%' },
  silent: { fill: 'fill-transparent',    stroke: 'stroke-amber-500',   open: 'No report in 90+ min', closed: 'No report in 90+ min' },
}

/**
 * The state a cell is in — and it changes meaning at the close of poll.
 *
 * While the polls are open the question is "is this cell keeping up", so the tone is the
 * gap against pace. Once they shut there is no pace to keep: every cell under 100% is
 * behind the rule, which would paint the entire map red and say nothing. After the close
 * the tone is the RESULT — the same distinction the Floor console makes when it withdraws
 * its critical ink once `perHourNeeded` goes null.
 */
export function toneOf(c: CellView, closed = false): CellTone {
  if ((c.stalledMinutes ?? 0) >= 90 && !closed) return 'silent'
  if (closed) return c.share >= 0.75 ? 'ahead' : c.share >= 0.5 ? 'onpace' : 'behind'
  if (c.behind > 0.05) return 'behind'
  if (c.share >= c.paceShare + 0.1) return 'ahead'
  return 'onpace'
}

export function GeoMap({
  cells, selected, onSelect, closed = false, height = 380,
}: {
  cells: CellView[]
  selected: string | null
  onSelect: (id: string | null) => void
  /** Past the close of poll the legend and the tones describe a result, not a race. */
  closed?: boolean
  height?: number
}) {
  const [hover, setHover] = useState<string | null>(null)
  const placed = useMemo(
    () => cells.filter((c) => c.latitude != null && c.longitude != null), [cells])
  const missing = cells.length - placed.length

  const geom = useMemo(() => {
    if (placed.length === 0) return null
    const lats = placed.map((c) => c.latitude!)
    const lons = placed.map((c) => c.longitude!)
    const midLat = (Math.min(...lats) + Math.max(...lats)) / 2
    const kx = Math.cos((midLat * Math.PI) / 180) || 1
    const xs = lons.map((l) => l * kx)
    /*
       The domain is the 5th-to-95th percentile, not the min and max, and that is the
       difference between a usable map and a dot in a corner.

       One cell sitting in another county — a mis-keyed centroid, or a genuinely remote
       station — stretches a min/max extent across a whole degree and squashes the
       twenty-seven cells that actually matter into a few pixels. A quantile domain frames
       where the operation IS; the handful outside are drawn pinned to the edge with a
       square marker and counted in the caption, so they are visible as "off this frame"
       rather than either dropped or allowed to destroy the frame.

       A single point, or a column of points, has zero extent on an axis; the floor keeps
       the divisor away from zero and centres the cluster rather than exploding it.
    */
    const span = (vs: number[]) => {
      const sorted = [...vs].sort((a, b) => a - b)
      const at = (q: number) => sorted[Math.min(sorted.length - 1,
        Math.max(0, Math.round(q * (sorted.length - 1))))]!
      let lo = at(0.05), hi = at(0.95)
      const d = hi - lo
      if (d < 1e-6) return { lo: lo - 0.001, hi: hi + 0.001 }
      // A tenth of the span as breathing room, so an edge cell is not clipped by the frame.
      return { lo: lo - d * 0.1, hi: hi + d * 0.1 }
    }
    return { x: span(xs), y: span(lats), kx }
  }, [placed])

  if (placed.length === 0) {
    return (
      <div className="p-4 space-y-2">
        <p className="text-erp-sm text-muted-foreground">
          None of the {cells.length} cells in view carries a coordinate, so there is nothing
          to place. A cell gets its centroid when it is formed from a mapped meeting point or
          when a coordinator captures a location from the field — until then the platform
          knows which station a cell belongs to, but not where it stands.
        </p>
        <p className="text-erp-xs text-muted-foreground/80">
          Drawing them in a grid would invent geography the platform does not hold, which is
          the one thing a map must never do.
        </p>
      </div>
    )
  }

  const W = 720, H = height, PAD = 26
  const fx = (c: CellView) => (c.longitude! * geom!.kx - geom!.x.lo) / (geom!.x.hi - geom!.x.lo)
  // Latitude increases northwards; screen y increases downwards. The subtraction is the
  // flip, and getting it wrong renders a map that is upside down but looks plausible.
  const fy = (c: CellView) => 1 - (c.latitude! - geom!.y.lo) / (geom!.y.hi - geom!.y.lo)
  const outside = (c: CellView) => fx(c) < 0 || fx(c) > 1 || fy(c) < 0 || fy(c) > 1
  const clamp = (v: number) => (v < 0 ? 0 : v > 1 ? 1 : v)
  const px = (c: CellView) => PAD + clamp(fx(c)) * (W - PAD * 2)
  const py = (c: CellView) => PAD + clamp(fy(c)) * (H - PAD * 2)
  const offFrame = placed.filter(outside)

  const maxMembers = Math.max(...placed.map((c) => c.members), 1)
  // Area, not radius, scales with size — a radius-scaled bubble overstates the big ones by
  // the square, which is the oldest bubble-chart error there is. The cap is deliberately
  // low: these marks overlap, and a 24px bubble in a tight catchment hides its neighbours.
  const r = (c: CellView) => 5 + Math.sqrt(c.members / maxMembers) * 10

  const active = hover ?? selected
  const shown = placed.find((c) => c.id === active)

  return (
    <div className="p-3 space-y-2">
      <div className="relative">
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto select-none"
             role="list" aria-label="Cells by location">
          <rect x={0} y={0} width={W} height={H} rx={8} className="fill-muted/30" />
          {[0.25, 0.5, 0.75].map((f) => (
            <g key={f} className="stroke-border/60" strokeWidth={1}>
              <line x1={PAD} x2={W - PAD} y1={PAD + f * (H - PAD * 2)} y2={PAD + f * (H - PAD * 2)} />
              <line y1={PAD} y2={H - PAD} x1={PAD + f * (W - PAD * 2)} x2={PAD + f * (W - PAD * 2)} />
            </g>
          ))}
          {placed.map((c) => {
            const t = TONE[toneOf(c, closed)]
            const on = c.id === active
            const off = outside(c)
            return (
              <g key={c.id} role="listitem">
                {/* Pinned to the edge and squared off, so it never reads as a measured
                    position inside the frame. */}
                {off && (
                  <rect x={px(c) - r(c) - 3} y={py(c) - r(c) - 3}
                        width={(r(c) + 3) * 2} height={(r(c) + 3) * 2} rx={2}
                        fill="none" className="stroke-foreground/40" strokeDasharray="2 2" />
                )}
                <circle
                  cx={px(c)} cy={py(c)} r={r(c)}
                  className={cn(t.fill, t.stroke, 'cursor-pointer transition-[r] duration-150')}
                  strokeWidth={on ? 3 : 2}
                  tabIndex={0}
                  onMouseEnter={() => setHover(c.id)}
                  onMouseLeave={() => setHover(null)}
                  onFocus={() => setHover(c.id)}
                  onBlur={() => setHover(null)}
                  onClick={() => onSelect(c.id === selected ? null : c.id)}
                  onKeyDown={(e) => { if (e.key === 'Enter') onSelect(c.id) }}
                >
                  <title>{`${c.code} — ${Math.round(c.share * 100)}% of ${c.members} voted`}</title>
                </circle>
                {/* The in-queue ring sits INSIDE the dot: people waiting at the station and
                    people who never left home need opposite responses from the reader. */}
                {c.inQueue > 0 && (
                  <circle cx={px(c)} cy={py(c)} r={Math.max(r(c) - 5, 2)} fill="none"
                          className="stroke-foreground/50" strokeWidth={1.5} strokeDasharray="3 3" />
                )}
              </g>
            )
          })}
        </svg>
        {shown && (
          <div className="pointer-events-none absolute left-3 top-3 rounded-md border border-border bg-popover px-3 py-2 text-erp-xs text-popover-foreground shadow-md">
            <div className="font-medium">{shown.code}{shown.label ? ` · ${shown.label}` : ''}</div>
            <div className="text-muted-foreground">{shown.stationName ?? 'Unnamed station'}</div>
            <div className="mt-1 tabular-nums">
              {shown.voted.toLocaleString()} / {shown.members.toLocaleString()} voted ·{' '}
              {Math.round(shown.share * 100)}% vs {Math.round(shown.paceShare * 100)}% pace
            </div>
            {shown.inQueue > 0 && <div className="tabular-nums">{shown.inQueue} in the queue</div>}
            {shown.stalledMinutes != null && (
              <div className="tabular-nums">Last report {shown.stalledMinutes} min ago</div>
            )}
            {shown.perHourNeeded != null && (
              <div className="tabular-nums">Needs {shown.perHourNeeded}/hour to finish</div>
            )}
          </div>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-erp-xs text-muted-foreground">
        {(Object.keys(TONE) as CellTone[]).map((k) => (
          <span key={k} className="inline-flex items-center gap-1.5">
            <svg width={12} height={12} aria-hidden>
              <circle cx={6} cy={6} r={5} className={cn(TONE[k].fill, TONE[k].stroke)} strokeWidth={2} />
            </svg>
            {closed ? TONE[k].closed : TONE[k].open}
          </span>
        ))}
        <span className="inline-flex items-center gap-1.5">
          <svg width={12} height={12} aria-hidden>
            <circle cx={6} cy={6} r={5} fill="none" className="stroke-foreground/50"
                    strokeWidth={1.5} strokeDasharray="3 3" />
          </svg>
          Dashed ring = members in the queue
        </span>
        <span>Bubble area = cell size</span>
        {offFrame.length > 0 && (
          <span>
            {offFrame.length} {offFrame.length === 1 ? 'cell sits' : 'cells sit'} outside this
            frame and {offFrame.length === 1 ? 'is' : 'are'} pinned to the edge (dashed square)
          </span>
        )}
        {missing > 0 && (
          <span className="text-amber-600 dark:text-amber-400">
            {missing} {missing === 1 ? 'cell has' : 'cells have'} no coordinate and cannot be placed
          </span>
        )}
      </div>
    </div>
  )
}
